/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  ControlPanelPage,
  CustomViews,
  EditorPage,
  EditorPageContextProvider,
  EntityLayout,
  extensionApiRef,
  FeatureRequestPanel,
  LifecyclePage,
  ReleasePage,
  ReleasePageContextProvider,
} from '@agilelab/plugin-wb-builder-catalog';
import {
  DEFAULT_NAMESPACE,
  parseEntityRef,
  RELATION_HAS_PART,
} from '@mainstage/catalog-model';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@mainstage/core-components';
import {
  catalogApiRef,
  entityRouteRef,
  useEntity,
  useRelatedEntities,
} from '@mainstage/plugin-catalog-react';
import { useMemo, useState } from 'react';

import {
  validateWitboostVersionedEntity,
  WitboostComponent,
  WitboostSystem,
} from '@agilelab/plugin-wb-builder-common';
import {
  BuilderCustomPagesEnum,
  BuilderSystemPageInputSchema,
  createRefreshEntityFunction,
  customAlertApiRef,
  customFrontendApiRef,
  CustomMicrofrontendComponent,
  CustomMicrofrontendOptions,
  useBuilderCustomPagesConfig,
  useMicrofrontendVisibility,
  useRefreshPermission,
  WbEnvironmentSelector,
  WbHeaderActions,
} from '@agilelab/plugin-wb-platform';
import {
  configApiRef,
  identityApiRef,
  useApi,
  useRouteRef,
} from '@mainstage/core-plugin-api';
import { useLocation, useNavigate } from 'react-router';
import { EntityLayoutWrapper } from './EntityLayoutWrapper';
import { CatalogClientExtension } from '@agilelab/plugin-wb-catalog-extension-common';
import useAsync from 'react-use/lib/useAsync';
import { TechdocsWrapper } from './TechdocsWrapper';
import {
  TeamRolesPanel,
  TeamRolesPanelContextProvider,
  useTeamRolesPanel,
} from '@agilelab/backstage-plugin-wb-team-roles';

type ProjectPageProps = {
  variant?: 'gridItem';
  title: string;
};

export function ProjectPage({
  variant = 'gridItem',
  title,
}: Readonly<ProjectPageProps>) {
  const { entity } = useEntity<WitboostSystem>();
  const versionedEntity = validateWitboostVersionedEntity(entity);
  const [version, setVersion] = useState(versionedEntity.spec.mesh.version);
  const entityRoute = useRouteRef(entityRouteRef);
  const navigate = useNavigate();
  const locations = useLocation();
  const configApi = useApi(configApiRef);
  const featureRequestEnabled =
    configApi.getOptionalBoolean('mesh.marketplace.featureRequest.enabled') ??
    false;
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const identityApi = useApi(identityApiRef);
  const catalogApiExt = useApi(extensionApiRef) as CatalogClientExtension;
  const customFrontendApi = useApi(customFrontendApiRef);
  const { entities, loading, error } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Component',
  });

  const customPagesConfiguration = useBuilderCustomPagesConfig(
    BuilderCustomPagesEnum.SystemPage,
  );

  const customPages: CustomMicrofrontendOptions[] = useMemo(() => {
    if (!customPagesConfiguration.data) return [];
    const getCustomFrontends = customFrontendApi.makeCustomMicrofrontends(
      customPagesConfiguration.data,
      BuilderSystemPageInputSchema,
    );
    return getCustomFrontends({ entity });
  }, [customPagesConfiguration.data, customFrontendApi, entity]);

  const { showPage } = useMicrofrontendVisibility(
    entity.spec.mesh.useCaseTemplateId,
    entity.spec.type,
  );

  const { value: processorErrors } = useAsync(async () => {
    const entityIds = entities?.flatMap(e =>
      e.metadata.uid ? [e.metadata.uid] : [],
    );
    entityIds?.push(entity.metadata.uid!);
    const { token } = await identityApi.getCredentials();
    const result = await catalogApiExt.getEntityErrors(entityIds!, { token });
    return (
      result
        .filter(r => r.errorMessage)
        .map(r => {
          const errorEntity = entities?.find(
            e => e.metadata.uid === r.entity_id,
          );
          return {
            message: r.errorMessage,
            entity: errorEntity ?? entity,
            parent: r.entity_id === entity.metadata.uid,
          };
        }) ?? []
    );
  }, [catalogApiExt, entities]);

  const { allowRefresh } = useRefreshPermission(entity);

  const refreshEntity = createRefreshEntityFunction({
    entity,
    catalogApi,
    alertApi,
  });
  const isEditorEnabled = configApi.getOptionalBoolean(
    'catalog.editor.enabled',
  );
  const isLifecycleEnabled =
    configApi.getOptionalBoolean(
      'mesh.builder.experimental.lifecyclePage.enabled',
    ) ?? false;

  const {
    isFeatureEnabled,
    canAccess,
    hasTroubleshootPermission: hasTroubleshootAccess,
    hasOwnerPermission: isOwner,
    hasOwnerLimitedPermission: isLimitedOwner,
    hasPrincipalOwner,
    isCatalogInfoOwner,
    isActualOwner,
    enabledTeamRoles,
    refresh: refreshTeamRoles,
    revokeAccess: revokeTeamRolesAccess,
  } = useTeamRolesPanel({ project: entity });

  const components = (entities || []) as WitboostComponent[];

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <InfoCard variant={variant} title={title}>
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  // For each component type we crate an independent table.
  // The table code is a replica of plugin-catalog/RelatedEntitiesCard.
  // We don't use RelatedEntitiesCard directly because here we load all entities just once
  // instead of doing it once for each component type.

  return (
    <EditorPageContextProvider
      entity={entity}
      relatedEntities={entities}
      setVersion={setVersion}
      version={version}
      processorErrors={processorErrors}
    >
      <EntityLayoutWrapper isWrappingProject version={version}>
        <EntityLayout.Route path="/" title="Overview">
          <CustomViews.DataProductPage
            data={{
              entity,
              componentsList: components,
              isEditorEnabled,
              allowRefresh,
            }}
            actions={{
              refreshEntity: refreshEntity,
              showComponent: (e: any) => {
                const data = {
                  name: e?.metadata.name || '',
                  kind: e?.kind,
                  namespace: e?.metadata.namespace || '',
                };

                data.kind = data.kind.toLocaleLowerCase('en-US');
                data.namespace =
                  data.namespace?.toLocaleLowerCase('en-US') ??
                  DEFAULT_NAMESPACE;

                const ref = parseEntityRef(data);
                const preUrl = locations.pathname.includes('my-projects')
                  ? '/my-projects'
                  : '';
                navigate(`${preUrl}${entityRoute(ref)}`);
              },
            }}
          />
        </EntityLayout.Route>
        <EntityLayout.Route path="/docs" title="Docs">
          <TechdocsWrapper entity={entity} />
        </EntityLayout.Route>

        {isEditorEnabled && (
          <EntityLayout.Route
            path="/editor"
            title="Edit and Test"
            overrideHeaderActions={
              <WbHeaderActions>
                <WbEnvironmentSelector />
              </WbHeaderActions>
            }
            tabProps={{ style: { textTransform: 'none' } }}
          >
            <EditorPage />
          </EntityLayout.Route>
        )}

        {isEditorEnabled && (
          <EntityLayout.Route path="/release" title="Deployment">
            <ReleasePageContextProvider
              entity={entity}
              relatedEntities={entities}
              setVersion={setVersion}
              processorErrors={processorErrors}
            >
              <ReleasePage />
            </ReleasePageContextProvider>
          </EntityLayout.Route>
        )}

        {!isEditorEnabled && (
          <EntityLayout.Route path="/deploy" title="Control Panel">
            <ControlPanelPage
              entity={versionedEntity}
              setVersion={setVersion}
            />
          </EntityLayout.Route>
        )}

        {isFeatureEnabled && canAccess && (
          <EntityLayout.Route path="/team-roles" title="Team Roles">
            <TeamRolesPanelContextProvider
              project={entity}
              isFeatureEnabled={isFeatureEnabled}
              hasTroubleshootAccess={hasTroubleshootAccess}
              isOwner={isOwner}
              isLimitedOwner={isLimitedOwner}
              hasPrincipalOwner={hasPrincipalOwner}
              isCatalogInfoOwner={isCatalogInfoOwner}
              enabledTeamRoles={enabledTeamRoles}
              refresh={refreshTeamRoles}
              revokeAccess={revokeTeamRolesAccess}
            >
              <TeamRolesPanel />
            </TeamRolesPanelContextProvider>
          </EntityLayout.Route>
        )}

        {customPages.map(
          page =>
            showPage(page) && (
              <EntityLayout.Route path={page.route} title={page.title}>
                <CustomMicrofrontendComponent key={page.identifier} {...page} />
              </EntityLayout.Route>
            ),
        )}

        {isLifecycleEnabled && (
          <EntityLayout.Route path="/lifecycle" title="Lifecycle">
            <LifecyclePage />
          </EntityLayout.Route>
        )}

        {isActualOwner && featureRequestEnabled && (
          <EntityLayout.Route path="/requests" title="Feature Requests">
            <FeatureRequestPanel />
          </EntityLayout.Route>
        )}
      </EntityLayoutWrapper>
    </EditorPageContextProvider>
  );
}
